import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, inject, OnInit, Output, signal } from '@angular/core'
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { distinctUntilChanged } from 'rxjs/operators'
import { CreditCard, CreditCardValidators } from 'angular-cc-library'
import { IPaymentCardSubmission } from '@appShared/interfaces/[Model-based]'
import { PaymentCardType_, PaymentCardTypes } from '@appShared/services/lookup/[CodeGen]'

@Component({
   selector: 'app-payment-card',
   templateUrl: './payment-card.component.html',
   styleUrls: ['./payment-card.component.less'],
   changeDetection: ChangeDetectionStrategy.OnPush,
   imports: [ReactiveFormsModule]
})
export class PaymentCardComponent implements OnInit {
   @Output() formReady = new EventEmitter<FormGroup>()

   public form!: FormGroup
   /** URL of the detected card-type image, or '' when none is recognized. */
   public readonly ccImgUrl = signal<string>('')

   private readonly _destroyRef = inject(DestroyRef)

   constructor(private readonly _fb: FormBuilder) {}

   ngOnInit(): void {
      // Expiration year is entered/stored as a 2-digit value (e.g. 27 => 2027).
      const currentYear = new Date().getFullYear()
      const currentTwoDigitYear = currentYear % 100

      this.form = this._fb.group(
         {
            cardNumber: ['', [Validators.required, <any>CreditCardValidators.validateCCNumber]],
            cardholderName: ['', [Validators.required, Validators.minLength(4), Validators.maxLength(100)]],
            expirationMonth: [null, [Validators.required, Validators.min(1), Validators.max(12)]],
            expirationYear: [null, [Validators.required, Validators.pattern(/^\d{2}$/), Validators.min(currentTwoDigitYear), Validators.max(currentTwoDigitYear + 20)]],
            cvv: ['', [Validators.required, Validators.pattern(/^\d{3,4}$/)]]
         },
         { validators: [PaymentCardComponent.notExpiredValidator()] }
      )

      this.formReady.emit(this.form)

      // Detect the card type from the number and surface its image (shown
      // inline with the Card Number label). '' when nothing is recognized.
      this.form.get('cardNumber')!.valueChanges.pipe(
         takeUntilDestroyed(this._destroyRef),
         distinctUntilChanged()
      ).subscribe((cardNumber: string) => {
         const cardType = cardNumber ? CreditCard.cardType(cardNumber) : ''
         this.ccImgUrl.set(cardType ? `/assets/images/cc/${cardType}.png` : '')
      })
   }

   /**
    * Maps the form to the canonical card submission: derives the card type code
    * from the PAN and builds an end-of-month MM/DD/YYYY expiration (matches the
    * legacy payment-auth submission shape).
    */
   getSubmission(): IPaymentCardSubmission {
      const value = this.form.getRawValue()
      const pan: string = value.cardNumber
      const cardType = pan ? CreditCard.cardType(pan) : ''
      const typeCode = PaymentCardTypes.find(type => type.literal.toLowerCase() === cardType)?.code
         ?? PaymentCardType_.Unknown

      // Expand the 2-digit year (e.g. 27) back to a full year (2027).
      const fullYear = 2000 + Number(value.expirationYear)
      const lastDay = new Date(fullYear, value.expirationMonth, 0).getDate()
      const expiration = `${String(value.expirationMonth).padStart(2, '0')}/${String(lastDay).padStart(2, '0')}/${fullYear}`

      return {
         cardholderName: value.cardholderName,
         cvv2: value.cvv,
         expiration,
         pan,
         typeCode
      } as IPaymentCardSubmission
   }

   /*
   * private methods
   * */

   private static luhnValidator(): ValidatorFn {
      return (control: AbstractControl): ValidationErrors | null => {
         const raw = (control.value ?? '').toString().replace(/\D/g, '')
         if (!raw) return null
         if (raw.length < 13 || raw.length > 19) return { cardNumber: true }

         let sum = 0
         let alternate = false
         for (let i = raw.length - 1; i >= 0; i--) {
            let n = parseInt(raw.charAt(i), 10)
            if (alternate) {
               n *= 2
               if (n > 9) n -= 9
            }
            sum += n
            alternate = !alternate
         }
         return sum % 10 === 0 ? null : { cardNumber: true }
      }
   }

   private static notExpiredValidator(): ValidatorFn {
      return (group: AbstractControl): ValidationErrors | null => {
         const month = group.get('expirationMonth')?.value
         const year = group.get('expirationYear')?.value
         if (!month || !year) return null

         // year is a 2-digit value (e.g. 27) — expand to a full year (2027).
         const fullYear = 2000 + Number(year)
         const now = new Date()
         const lastDayOfMonth = new Date(fullYear, month, 0)
         return lastDayOfMonth < now ? { cardExpired: true } : null
      }
   }
}
